/* eslint-disable max-classes-per-file */
import { action, makeObservable, observable } from 'mobx';
import ModelPerformances from '../ModelPerformances';
import axios from 'axios';
import { ModelType } from '../ModelItem';

export class EnsembleWeight {
  [tree: string]: number;
}

export type EnsembleThreshold = Record<string, number>;

export class BehavioralThresholdsDecile {
  thresholdMin: number;

  thresholdMax: number;

  population: number;

  conversions: number;

  tile: string;
}

export class EnsemblePerformance {
  weights: EnsembleWeight;

  thresholds: EnsembleThreshold;

  performances: ModelPerformances;

  constructor(
    weights: EnsembleWeight,
    thresholds: EnsembleThreshold,
    performances: ModelPerformances
  ) {
    this.weights = weights;
    this.thresholds = thresholds;
    this.performances = performances;
    makeObservable(this, {
      weights: observable,
      thresholds: observable,
      performances: observable,
    });
  }
}

export class BehavioralEnsemblePerformance {
  behavioralThresholdsDeciles: BehavioralThresholdsDecile[];

  thresholds: EnsembleThreshold;

  performances: ModelPerformances;

  constructor(
    behavioralThresholdsDeciles: BehavioralThresholdsDecile[],
    performances: ModelPerformances,
    threshold: EnsembleThreshold
  ) {
    this.behavioralThresholdsDeciles = behavioralThresholdsDeciles;
    this.performances = performances;
    this.thresholds = threshold;
    makeObservable(this, {
      behavioralThresholdsDeciles: observable,
      performances: observable,
      thresholds: observable,
    });
  }
}

export class Ensemble {
  error: string;

  weights: EnsembleWeight;

  thresholds: EnsembleThreshold;

  performances: EnsemblePerformance;

  behavioralPerformances: BehavioralEnsemblePerformance;

  constructor(
    weights: EnsembleWeight = {},
    thresholds: EnsembleThreshold = { 'very good': 0, good: 0, medium: 0 },
    performances: EnsemblePerformance = null,
    behavioralPerformances: BehavioralEnsemblePerformance = null
  ) {
    this.error = null;
    this.weights = weights;
    this.thresholds = thresholds;
    this.performances = performances;
    this.behavioralPerformances = behavioralPerformances;
    makeObservable(this, {
      error: observable,
      weights: observable,
      thresholds: observable,
      performances: observable,
      behavioralPerformances: observable,
    });
  }
}

export class EnsemblingPageModel {
  ensemble: Ensemble;

  loadingEnsemblingPerformances: boolean;

  fetchingBestThresholds: boolean;

  ensemblingError: string;

  constructor(other?: EnsemblingPageModel) {
    if (other) {
      this.ensemble = other.ensemble;
      this.loadingEnsemblingPerformances = other.loadingEnsemblingPerformances;
      this.ensemblingError = other.ensemblingError;
      this.fetchingBestThresholds = other.fetchingBestThresholds;
    } else {
      this.ensemble = null;
      this.loadingEnsemblingPerformances = false;
      this.fetchingBestThresholds = false;
      this.ensemblingError = null;
    }
    makeObservable(this, {
      ensemble: observable,
      loadingEnsemblingPerformances: observable,
      ensemblingError: observable,
      fetchingBestThresholds: observable,
      loadConfiguration: action,
      saveConfiguration: action,
      loadPerformance: action,
      auto: action,
    });
  }

  async loadConfiguration(tenant: number, modelId: number) {
    const {
      data: [weights, thresholds],
    } = await axios.get(
      `/api/tenant/${tenant}/model/${modelId}/ensembling/configuration`
    );
    const ensemble = new Ensemble(weights, thresholds);
    this.ensemble = ensemble;
    this.ensemble = ensemble;
  }

  /**
   * Persist weights and thresholds, and compute nothing.
   *
   * `loadPerformance` also saves, because the route it calls writes before it
   * recomputes, but it costs a full rebuild of the training-set scoring to
   * return numbers the revamped page has already derived from the leaf grid.
   * That cost is what a "Save and compute" button existed to ration.
   *
   * Failure is recorded and not thrown. This runs on a settled drag rather than
   * on a click, so there is no action for an exception to abort: the caller has
   * already moved on and the page shows `ensemblingError` instead.
   */
  async saveConfiguration(tenant: number, modelId: number) {
    this.ensemblingError = null;
    try {
      await axios.put(
        `/api/tenant/${tenant}/model/${modelId}/ensembling/configuration`,
        {
          weights: this.ensemble.weights,
          thresholds: this.ensemble.thresholds,
        }
      );
    } catch (err) {
      this.ensemblingError = err.response
        ? err.response?.data?.message
        : err?.message;
    }
  }

  async loadPerformance(
    tenant: number,
    modelId: number,
    options: {
      isProcessing: boolean;
      modelType: ModelType;
    }
  ) {
    const { isProcessing, modelType } = options;
    this.loadingEnsemblingPerformances = true;
    this.ensemblingError = null;
    try {
      const { data: ensembling } = await axios.put(
        `/api/tenant/${tenant}/model/${modelId}/ensembling`,
        {
          weights: this.ensemble.weights,
          thresholds: this.ensemble.thresholds,
        },
        { params: { isProcessing } }
      );
      if (modelType === 'customer_fit') {
        this.ensemble.performances = new EnsemblePerformance(
          ensembling.weights,
          ensembling.thresholds,
          ensembling.performances
        );
      } else {
        this.ensemble.behavioralPerformances =
          new BehavioralEnsemblePerformance(
            ensembling.behavioralThresholdsDeciles,
            ensembling.performances,
            ensembling.thresholds
          );
      }
    } catch (err) {
      this.ensemblingError = err.response
        ? err.response?.data?.message
        : err?.message;
    }
    this.loadingEnsemblingPerformances = false;
  }

  async auto(
    tenant: number,
    modelId: number,
    options: { isProcessing: boolean; modelType: ModelType }
  ) {
    const { isProcessing, modelType } = options;
    this.fetchingBestThresholds = true;
    this.ensemblingError = null;
    try {
      const { data: ensembling } = await axios.put(
        `/api/tenant/${tenant}/model/${modelId}/ensembling/auto`,
        null,
        { params: { isProcessing } }
      );
      if (modelType === 'customer_fit') {
        this.ensemble = {
          weights: ensembling.weights,
          thresholds: ensembling.thresholds,
          performances: new EnsemblePerformance(
            ensembling.weights,
            ensembling.thresholds,
            ensembling.performances
          ),
          behavioralPerformances: null,
          error: null,
        };
      } else {
        this.ensemble = {
          behavioralPerformances: new BehavioralEnsemblePerformance(
            ensembling.behavioralThresholdsDeciles,
            ensembling.performances,
            ensembling.thresholds
          ),
          thresholds: ensembling.thresholds,
          weights: null,
          error: null,
          performances: null,
        };
      }
    } catch (err) {
      this.ensemblingError = err.response
        ? err.response?.data?.message
        : err?.message;
    }

    this.fetchingBestThresholds = false;
  }
}

/**
 * How far a weight allocation may sit from a total of 1 and still be stored.
 *
 * Floating point, not slack. Summed across all 5151 integer three-way
 * allocations the largest error is 1.1e-16, so this admits every allocation the
 * bar can produce and nothing anyone would call an unbalanced model.
 *
 * It lives here because the button, the autosave path and the save route each
 * decide the same question and must decide it identically. They did not: the
 * front compared the total to 100 exactly, which 12 of those 5151 allocations
 * fail (14/55/31 sums to 100.00000000000001). On the unflagged page that only
 * disabled a button. On an autosaving page it silently dropped the write.
 */
export const WEIGHT_TOTAL_TOLERANCE = 1e-9;

/**
 * Whether an allocation of tree weights totals 1, within floating point.
 *
 * An empty or absent allocation is not an answer to this question, so it
 * reports `true`: a model with no trees is configured through the threshold
 * fields alone and has no allocation to be wrong about.
 */
export function weightsTotalOne(
  weights: Record<string, number> | null | undefined
): boolean {
  const values = Object.values(weights ?? {});
  if (!values.length) return true;
  const total = values.reduce((sum, weight) => sum + Number(weight), 0);
  return Math.abs(total - 1) <= WEIGHT_TOTAL_TOLERANCE;
}
